import React, { useEffect, useMemo, useState } from "react";
import "./AdminCohort.scss";
import { useDispatch, useSelector } from "react-redux";
import AdminCreateCohortModal from "./AdminCohortModal.js";
import ToastMessage from "../../utils/toasts/toastMessage.js";
import { getCSVUploadedData } from "../../Redux/Admin_Panel/people/people.action.js";
import {
  deleteCohort,
  getCohortGroups,
} from "../../Redux/Admin_Panel/cohort/cohort.action.js";
import { formatDateandTime, truncateText } from "../../utils/utils.js";
import Loader from "../../utils/loader/loader.js";
import { getSchoolList } from "../../Redux/masterData/masterData.action.js";
import DeleteConfirmModal from "../../Users/common/deleteConfirmModal/deleteConfirmModal.js";

const PAGE_SIZE_OPTIONS = [3, 6, 12, 18];
const DEFAULT_PAGE_SIZE = 3;

// Builds [1, 2, 3, "…", 10] style page lists
const getPageItems = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  let start = Math.max(2, current - 1);
  let end = Math.min(total - 1, current + 1);
  if (current <= 3) {
    start = 2;
    end = 4;
  } else if (current >= total - 2) {
    start = total - 3;
    end = total - 1;
  }

  const items = [1];
  if (start > 2) items.push("start-ellipsis");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < total - 1) items.push("end-ellipsis");
  items.push(total);
  return items;
};

const AdminCohortList = () => {
  const dispatch = useDispatch();
  const [hiddenIds, setHiddenIds] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedCohortId, setSelectedCohortId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterSchool, setFilterSchool] = useState("");
  const [filterClass, setFilterClass] = useState("");
  const [filterCohortName, setFilterCohortName] = useState("");

  const [loading, setLoading] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    message: "",
    type: "",
  });

  const studentList =
    useSelector((s) => s?.AdminPeopleManager?.csvData?.users) || [];
  const cohortStore =
    useSelector((s) => s?.AdminCohortManager?.cohortList?.groups) || [];
  const schoolStore =
    useSelector((s) => s?.MasterDataManager?.schoolList?.schools) || [];

  const schoolNameBySchoolId = useMemo(() => {
    const map = {};
    schoolStore.forEach((s) => {
      if (s?.school_id) map[s.school_id] = s.school_name;
    });
    return map;
  }, [schoolStore]);

  const getCohortSchoolName = (cohort) =>
    schoolNameBySchoolId[cohort?.school_id] || cohort?.school_name || "—";

  const cohortList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return cohortStore
      .filter((c) => !hiddenIds.includes(c.id))
      .filter((c) => {
        if (!filterSchool) return true;
        return getCohortSchoolName(c) === filterSchool;
      })
      .filter((c) => {
        if (!filterClass) return true;
        return `${c?.grade}|${c?.division}` === filterClass;
      })
      .filter((c) => {
        if (!filterCohortName) return true;
        return c?.group_name === filterCohortName;
      })
      .filter((c) => {
        if (!q) return true;
        const schoolName = getCohortSchoolName(c).toLowerCase();
        const classLabel =
          `${c?.grade || ""}${c?.division || ""}`.toLowerCase();
        const cohortName = (c?.group_name || "").toLowerCase();
        return (
          schoolName.includes(q) ||
          classLabel.includes(q) ||
          cohortName.includes(q)
        );
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [
    cohortStore,
    hiddenIds,
    searchQuery,
    filterSchool,
    filterClass,
    filterCohortName,
    schoolNameBySchoolId,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterSchool, filterClass, filterCohortName]);

  // ------------------------ Filters logic starts here -----------------------------------------------
  const schoolFilterOptions = useMemo(() => {
    const set = new Set();
    cohortStore.forEach((c) => set.add(getCohortSchoolName(c)));
    return Array.from(set)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }, [cohortStore, schoolNameBySchoolId]);

  const classFilterOptions = useMemo(() => {
    const set = new Set();
    cohortStore.forEach((c) => {
      if (c?.grade && c?.division) set.add(`${c.grade}|${c.division}`);
    });
    return Array.from(set)
      .map((key) => {
        const [grade, division] = key.split("|");
        return { value: key, label: `${grade}${division}`, grade, division };
      })
      .sort(
        (a, b) =>
          a.grade.localeCompare(b.grade, undefined, { numeric: true }) ||
          a.division.localeCompare(b.division),
      );
  }, [cohortStore]);

  const cohortNameFilterOptions = useMemo(() => {
    const set = new Set();
    cohortStore.forEach((c) => c?.group_name && set.add(c.group_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [cohortStore]);

  // ------------------------ Filters logic ends here ------------------------------------------------

  // ------------------------ Pagination logic starts here -------------------------------------------
  const totalItems = cohortList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  const paginatedList = useMemo(
    () => cohortList.slice(startIndex, startIndex + pageSize),
    [cohortList, startIndex, pageSize],
  );

  const pageItems = useMemo(
    () => getPageItems(currentPage, totalPages),
    [currentPage, totalPages],
  );

  const goToPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };
  // ------------------------ Pagination logic ends here --------------------------------------------

  useEffect(() => {
    dispatch(getCSVUploadedData());
    dispatch(getCohortGroups());
    dispatch(getSchoolList());
  }, [dispatch]);

  const handleOpenCreate = () => {
    setSelectedCohort(null);
    setEditing(false);
    setShowModal(true);
  };

  const handleEdit = (cohort) => {
    setSelectedCohort(cohort);
    setEditing(true);
    setShowModal(true);
  };

  const handleDelete = (e, cohortId) => {
    e.preventDefault();
    setSelectedCohortId(cohortId);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCohortId) return;
    setLoading(true);
    try {
      const result = await dispatch(deleteCohort(selectedCohortId));
      if (result?.success) {
        await dispatch(getCohortGroups());
        setShowConfirmModal(false);
        setLoading(false);
        setToastState({
          show: true,
          message: result?.message || "Cohort deleted successfully.",
          type: "error",
        });
      } else {
        setToastState({
          show: true,
          message: result?.message || "Unable to delete cohort.",
          type: "success",
        });
      }
    } catch {
      setToastState({
        show: true,
        message: "Something went wrong.",
        type: "success",
      });
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setSelectedCohortId(null);
    }
  };

  const handleCohortSaved = () => {
    setPage(1); // newest cohort is first
    dispatch(getCohortGroups());
  };

  const hasCohorts = totalItems > 0;

  return (
    <>
      <div className="overflow-hidden inner-page-content">
        <div className="create-selection-container">
          {hasCohorts || cohortStore?.length > 0 ? (
            <div className="cohort-list-wrapper">
              <div className="sticky-header-section">
                <h5>Cohorts: {totalItems}</h5>
                <div className="button-group">
                  <button className="btn-add-test" onClick={handleOpenCreate}>
                    + Add New Cohort
                  </button>
                </div>
              </div>

               {/* Filters row */}
              <div className="cohort-filters-row">
                <div className="cohort-search-box">
                  <input
                    type="text"
                    placeholder="search cohort or school name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="cohort-search-input"
                  />
                </div>

                <select
                  className="cohort-filter-select"
                  value={filterSchool}
                  onChange={(e) => setFilterSchool(e.target.value)}
                >
                  <option value="">All Schools</option>
                  {schoolFilterOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>

                <select
                  className="cohort-filter-select"
                  value={filterClass}
                  onChange={(e) => setFilterClass(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {classFilterOptions.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>

                <select
                  className="cohort-filter-select"
                  value={filterCohortName}
                  onChange={(e) => setFilterCohortName(e.target.value)}
                >
                  <option value="">All Cohorts</option>
                  {cohortNameFilterOptions.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>

                {(searchQuery ||
                  filterSchool ||
                  filterClass ||
                  filterCohortName) && (
                  <button
                    type="button"
                    className="btn-outlined btn-outlined--dark cohort-filter-clear"
                    onClick={() => {
                      setSearchQuery("");
                      setFilterSchool("");
                      setFilterClass("");
                      setFilterCohortName("");
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
              
              <div className="scrollable-content-box">
                <div className="list-gap-wrapper">
                  {paginatedList?.map((cohort) => (
                    <div className="cohort-summary-bar" key={cohort.id}>
                      <div
                        className="cohort-badge"
                        title={`Class ${cohort?.grade}${cohort?.division}`}
                      >
                        <span className="cohort-badge-caption">Class</span>
                        <span className="cohort-badge-value">
                          {cohort?.grade}
                          {cohort?.division}
                        </span>
                      </div>

                      <div className="cohort-field cohort-field--name">
                        <span className="cohort-field-label">Cohort Name</span>
                        <span
                          className="cohort-field-value cohort-field-value"
                          title={cohort?.group_name}
                        >
                          {cohort?.group_name || "—"}
                        </span>
                      </div>

                      <div className="cohort-field cohort-field--school">
                        <span className="cohort-field-label">School</span>
                        <span className="cohort-field-value">
                          {truncateText(getCohortSchoolName(cohort), 40)}
                        </span>
                      </div>

                      <div className="cohort-field cohort-field--students">
                        <span className="cohort-field-label">Students</span>
                        <span className="cohort-field-value">
                          <span className="students-pill">
                            {(cohort?.student_id || []).length}
                          </span>
                        </span>
                      </div>

                      <div className="cohort-field cohort-field--created">
                        <span className="cohort-field-label">Created At</span>
                        <span className="cohort-field-value">
                          {formatDateandTime(cohort?.created_at)}
                        </span>
                      </div>

                      <div className="cohort-actions">
                        <button
                          className="btn-outlined btn-outlined--dark"
                          onClick={() => handleEdit(cohort)}
                        >
                          Edit Members
                        </button>
                        <button
                          className="btn-outlined btn-outlined--dark"
                          onClick={(e) => handleDelete(e, cohort.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination footer */}
              <div className="pagination-bar">
                <div className="pagination-info">
                  Showing <strong>{startIndex + 1}</strong>–
                  <strong>{endIndex}</strong> of <strong>{totalItems}</strong>
                </div>

                <nav className="pagination-controls" aria-label="Cohort pages">
                  <button
                    className="page-btn"
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    aria-label="First page"
                  >
                    «
                  </button>
                  <button
                    className="page-btn"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>

                  {pageItems.map((item) =>
                    typeof item === "number" ? (
                      <button
                        key={item}
                        className={`page-btn ${
                          item === currentPage ? "active" : ""
                        }`}
                        onClick={() => goToPage(item)}
                        aria-current={item === currentPage ? "page" : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={item} className="page-ellipsis">
                        …
                      </span>
                    ),
                  )}

                  <button
                    className="page-btn"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    ›
                  </button>
                  <button
                    className="page-btn"
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    aria-label="Last page"
                  >
                    »
                  </button>
                </nav>

                <label className="page-size">
                  Rows per page
                  <select value={pageSize} onChange={handlePageSizeChange}>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          ) : (
            <div className="empty-state-wrapper">
              <div className="empty-state-header">
                <h2 className="title">Create your first cohort</h2>
              </div>

              <div className="cards-wrapper">
                <div
                  className="selection-card test-card"
                  onClick={handleOpenCreate}
                >
                  <div className="icon">👥</div>
                  <h5>Add New Cohort</h5>
                  <p>Pick a class and division, then assign students</p>
                </div>
              </div>
            </div>
          )}
        </div>

               <DeleteConfirmModal
  show={showConfirmModal}
  message="Are you sure you want to delete this cohort?"
  loading={loading}
  onCancel={() => {
    setShowConfirmModal(false);
    setSelectedCohortId(null);
  }}
  onConfirm={handleConfirmDelete}
/>

        <Loader show={loading} />

        <ToastMessage
          show={toastState.show}
          onClose={() => setToastState({ show: false, message: "", type: "" })}
          message={toastState.message}
          type={toastState.type}
        />
      </div>

      <AdminCreateCohortModal
        show={showModal}
        onHide={() => setShowModal(false)}
        schoolList={schoolStore}
        students={studentList}
        cohortData={selectedCohort}
        editing={editing}
        onSaved={handleCohortSaved}
      />
    </>
  );
};

export default AdminCohortList;
